import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "motion/react";
import { useTranslation } from "react-i18next";

import type { DataSlot } from "../OnboardingAPI";
import ClosedScene from "./ClosedScene";
import ProgressHeader from "./ProgressHeader";
import { LoadingPage, PageFrame, ProblemPage, SaveIndicator } from "./chrome";
import { useOnboardingHead } from "./useOnboardingHead";
import { EASE_ARRAY, sessionFlag, useStaging, usePrefersReducedMotion } from "./calm";
import { StepCard, type PanelKey } from "./StepCard";
import TitleCard, { type SceneKey } from "./TitleCard";
import WelcomeScene from "./WelcomeScene";
import "./onboarding.css";
import { buildSteps, firstIncomplete, hasStarted, hashFor, isComplete, stepFromHash, type Progress, type StepDef } from "./steps";
import CompanyStep from "./steps/CompanyStep";
import OptionalStep from "./steps/OptionalStep";
import PhoneStep from "./steps/PhoneStep";
import PropertiesStep from "./steps/PropertiesStep";
import QuestionsStep from "./steps/QuestionsStep";
import ReviewStep, { SubmitLabel } from "./steps/ReviewStep";
import SlotStep from "./steps/SlotStep";
import type { StepContext } from "./steps/shared";
import { useOnboardingController, type OnboardingController } from "./useOnboardingController";

/** The server's slug for a step (a positive list there): welcome, company, properties, <slot>, more, questions, phone, review. */
export const apiSlugOf = (step: StepDef): string | null => {
  switch (step.kind) {
    case "welcome": return "welcome";
    case "company": return "company";
    case "slot": return step.slot === "escapePlans" ? "escape" : (step.slot ?? null);
    case "optional": return "more";
    case "questions": return "questions";
    case "properties": return "properties";
    case "phone": return "phone";
    case "review": return "review";
    default: return null;
  }
};

const panelOf = (step: StepDef): PanelKey | null => {
  switch (step.kind) {
    case "company": return "company";
    case "properties": return "properties";
    case "slot": return (step.slot as PanelKey) ?? "buildings";
    case "optional": return "optional";
    case "questions": return "questions";
    // the phone check and the review stand alone: the card in the middle, no panel
    default: return null;
  }
};

/** The scene (title card) that opens a phase: the company, the first data step, the review. */
const sceneFor = (step: StepDef, steps: StepDef[]): SceneKey | null => {
  if (step.kind === "company") return "company";
  if (step.kind === "review") return "review";
  if (step.kind === "slot" && steps.find((candidate) => candidate.kind === "slot")?.index === step.index) return "data";
  return null;
};

interface Saved {
  max: number;
  skipped: DataSlot[];
}

const readSaved = (token: string): Saved => {
  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(`onboarding.step.${token}`) ?? "null");
    if (parsed && typeof parsed.max === "number") return { max: parsed.max, skipped: Array.isArray(parsed.skipped) ? parsed.skipped : [] };
  } catch {
    // unreadable: start fresh
  }
  return { max: 0, skipped: [] };
};

/**
 * Public onboarding page (`/onboarding/:token`): a guided flow, one step at a time. Welcome, company,
 * one step for each required data slot, the optional ones together, the questions one by one, the
 * review - and, once submitted, nothing but the status scene. Every step lives in one card of fixed
 * size with the explanation and a hairline figure beside it.
 *
 * Where the person is lives in the address (`#/anlagen`) and in sessionStorage; a reload or the
 * browser's back button work. Contract: tri-server `docs/onboarding-leads.md`.
 */
export default function OnboardingPage() {
  const c = useOnboardingController();
  const { t } = c;
  useOnboardingHead();

  if (c.problem) return <ProblemPage problem={c.problem} onReload={() => window.location.reload()} />;
  if (c.slim) {
    return (
      <PageFrame>
        <ClosedScene slim={c.slim} token={c.token} accessSlow={c.accessSlow} />
      </PageFrame>
    );
  }
  if (!c.view || !c.values) return <LoadingPage />;
  return <Flow c={c} t={t} />;
}

function Flow({ c, t }: { c: OnboardingController; t: (key: string, options?: Record<string, unknown>) => string }) {
  const { i18n } = useTranslation();
  const reduced = usePrefersReducedMotion();
  // the staging (intro, title cards, the house) plays, except for reduced motion and data saver
  const show = useStaging();
  const navigate = useNavigate();
  const location = useLocation();
  const view = c.view!;
  const values = c.values!;
  const steps = useMemo(() => buildSteps(view), [view.slots, !!view.phone]);
  const saved = useRef(readSaved(c.token));
  const [skipped, setSkipped] = useState<Set<DataSlot>>(() => new Set(saved.current.skipped));
  const [maxReached, setMaxReached] = useState(saved.current.max);
  const [nudge, setNudge] = useState<string | null>(null);
  const [scene, setScene] = useState<SceneKey | null>(null);
  const [direction, setDirection] = useState(1);
  const [navigated, setNavigated] = useState(false);
  const backOverride = useRef<(() => boolean) | null>(null);

  const progress: Progress = { values, view, skipped };
  const firstOpen = firstIncomplete(steps, progress);
  const asked = stepFromHash(location.hash, steps);
  // The review (and with it the submit) is not reachable while the mobile number is not confirmed - not by the address,
  // not by a dot of the progress bar: those lead to the step that confirms it.
  const phoneStep = steps.find((step) => step.kind === "phone");
  const reviewBlocked = !!phoneStep && !view.phone?.verified;
  const requested = asked?.kind === "review" && reviewBlocked ? phoneStep : asked;
  // A step can be reached once everything before it is in order, or once it was visited in this session.
  const allowedMax = Math.max(maxReached, firstOpen.index);
  const current = requested && requested.kind !== "done" && requested.index <= allowedMax ? requested : steps[0];
  const started = hasStarted(view);

  // an address that asks for a step that is not reachable (yet) goes to where the person can be
  useEffect(() => {
    if (requested && requested.kind !== "done" && requested.index > allowedMax) navigate({ hash: hashFor(firstOpen) }, { replace: true });
    else if (asked && requested && asked !== requested) navigate({ hash: hashFor(requested) }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash, reviewBlocked]);

  // remember where we got to
  useEffect(() => {
    const index = current.index;
    setMaxReached((previous) => Math.max(previous, index));
  }, [current.index]);
  useEffect(() => {
    try {
      window.sessionStorage.setItem(`onboarding.step.${c.token}`, JSON.stringify({ max: maxReached, skipped: Array.from(skipped) }));
    } catch {
      // no storage
    }
  }, [maxReached, skipped, c.token]);

  // tell the server which step was entered (once per step and session)
  useEffect(() => {
    const slug = apiSlugOf(current);
    if (slug) c.reportStep(slug);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current.index]);

  // a title card the first time a phase is reached (and only with the show on)
  useEffect(() => {
    const key = sceneFor(current, steps);
    if (!key || !show) return;
    const flag = sessionFlag(`onboarding.scene.${key}.${c.token}`);
    if (flag.seen()) return;
    flag.mark();
    setScene(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current.index, show]);
  useEffect(() => {
    if (!show) setScene(null);
  }, [show]);

  const go = useCallback(
    (wanted: StepDef, note?: string) => {
      const step = wanted.kind === "review" && reviewBlocked && phoneStep ? phoneStep : wanted;
      setDirection(step.index >= current.index ? 1 : -1);
      setNudge(note ?? null);
      setNavigated(true);
      navigate({ hash: hashFor(step) });
    },
    [current.index, navigate, reviewBlocked, phoneStep],
  );
  const next = useCallback(() => {
    const target = steps[current.index + 1];
    if (target && target.kind !== "done") go(target);
  }, [current.index, go, steps]);
  const back = useCallback(() => {
    if (backOverride.current?.()) return;
    const target = steps[current.index - 1];
    if (target) go(target);
  }, [current.index, go, steps]);

  const ctx: StepContext = {
    c,
    steps,
    step: current,
    skipped,
    skip: (slot) => setSkipped((previous) => new Set(previous).add(slot)),
    next,
    back,
    goTo: go,
    focus: navigated,
    nudge,
    clearNudge: () => setNudge(null),
    setBackOverride: (handler) => {
      backOverride.current = handler;
    },
  };

  // the submit: a gap sends the person back, kindly, to the first step that is missing something
  const submitting = useRef(false);
  const submit = () => {
    if (submitting.current || c.submitting) return;
    const blockers = c.blockers;
    if (blockers.includes("uploads")) return setNudge(t("onboardingPage.nudge.uploads"));
    const company = steps.find((step) => step.kind === "company")!;
    if (blockers.some((blocker) => blocker === "companyName" || blocker === "contactFirstName" || blocker === "contactLastName" || blocker === "contactPhone")) return go(company, t("onboardingPage.nudge.company"));
    if (blockers.includes("phone") && phoneStep) return go(phoneStep, t("onboardingPage.nudge.phone"));
    if (blockers.includes("fields")) return go(company, t("onboardingPage.nudge.fields"));
    if (blockers.includes("data")) {
      const firstSlot = steps.find((step) => step.kind === "slot");
      if (firstSlot) return go(firstSlot, t("onboardingPage.nudge.data"));
    }
    submitting.current = true;
    void c
      .submit()
      .then((result) => {
        // the server does not count the number as confirmed: back to the step that does it
        if (result === "phone_not_verified" && phoneStep) go(phoneStep, t("onboardingPage.nudge.phone"));
      })
      .finally(() => {
        submitting.current = false;
      });
  };

  const titleOf = useCallback(
    (step: StepDef) => {
      switch (step.kind) {
        case "company": return t("onboardingPage.titles.company");
        case "properties": return t("onboardingPage.titles.properties");
        case "slot": return t(`onboardingPage.headings.${step.slot}`, { defaultValue: step.slot });
        case "optional": return t("onboardingPage.titles.optional");
        case "questions": return t("onboardingPage.titles.questions");
        case "phone": return t("onboardingPage.titles.phone");
        case "review": return t("onboardingPage.titles.review");
        default: return "";
      }
    },
    [t],
  );

  // keyboard: Enter presses the main button (never the one that sends the form), Esc goes back, the arrows move
  // between steps - and none of it while something is being typed, chosen or opened
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      // a held key repeats: it must never act more than once
      if (event.repeat || event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName;
      // the code field of the phone step keeps Enter, Esc and the arrows for itself
      if (target?.closest?.("[data-keys-own]")) return;
      const editing = tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || !!target?.isContentEditable;
      // a group of choices, a menu or an open dropdown owns its keys (arrows, Esc)
      const owned = !!target?.closest?.('[role="radiogroup"], [role="listbox"], [role="menu"], [role="tablist"], [role="combobox"], [role="slider"], [aria-expanded="true"]');
      if (document.querySelector('[role="dialog"], [role="alertdialog"], [role="listbox"]')) return;
      if (scene) {
        if (event.key === "Enter" || event.key === "Escape" || event.key === " ") {
          event.preventDefault();
          setScene(null);
        }
        return;
      }
      if (event.key === "Enter") {
        if (tag === "TEXTAREA" || tag === "BUTTON" || tag === "A" || (tag === "INPUT" && (target as HTMLInputElement).type === "file")) return;
        // the review's button sends the data for good: only a click, or Enter/Space on the focused button, may press it
        if (current.kind === "review") return;
        const primary = document.querySelector<HTMLButtonElement>("[data-primary]:not([disabled])");
        if (primary) {
          event.preventDefault();
          primary.click();
        }
      } else if (event.key === "Escape") {
        if (editing || owned) return;
        if (current.kind !== "welcome") {
          event.preventDefault();
          back();
        }
      } else if ((event.key === "ArrowRight" || event.key === "ArrowLeft") && !editing && !owned) {
        const primary = document.querySelector<HTMLButtonElement>("[data-primary]:not([disabled])");
        if (event.key === "ArrowRight" && primary && current.kind !== "review") {
          event.preventDefault();
          primary.click();
        } else if (event.key === "ArrowLeft" && current.kind !== "welcome") {
          event.preventDefault();
          back();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [back, current.kind, scene]);

  // a step counts as done when its work is in; the optional page once it has been passed
  const stepDone = (step: StepDef) => (step.kind === "optional" ? step.index < maxReached : step.kind === "review" ? false : isComplete(step, progress));
  const panel = panelOf(current);
  const offset = reduced ? 0 : 44;

  // the resume hint on the landing page: where the person would continue
  const resumeAt = started && firstOpen.kind !== "welcome" ? titleOf(firstOpen) : undefined;

  const body = () => {
    switch (current.kind) {
      case "company": return <CompanyStep ctx={ctx} />;
      case "properties": return <PropertiesStep ctx={ctx} />;
      case "slot": return <SlotStep ctx={ctx} />;
      case "optional": return <OptionalStep ctx={ctx} />;
      case "questions": return <QuestionsStep ctx={ctx} />;
      case "phone": return <PhoneStep ctx={ctx} />;
      case "review": return <ReviewStep ctx={ctx} onSubmit={submit} />;
      default: return null;
    }
  };

  const showingScene = !!scene && current.kind !== "welcome";

  return (
    <PageFrame
      status={<SaveIndicator status={c.saveStatus} onRetry={() => void c.flush()} />}
      progress={current.kind === "welcome" ? null : <ProgressHeader steps={steps} current={current} maxReached={Math.max(maxReached, current.index)} complete={stepDone} onGo={(step) => go(step)} titleOf={titleOf} />}
    >
      {current.kind === "welcome" ? (
        <WelcomeScene
          key={i18n.language}
          token={c.token}
          contactName={c.greetingContact}
          company={c.greetingName}
          expiresAt={view.expiresAt}
          welcome={view.welcome}
          resumeAt={resumeAt}
          onStart={() => go(started ? firstOpen : steps[1])}
        />
      ) : showingScene ? (
        <TitleCard scene={scene!} onDone={() => setScene(null)} />
      ) : (
        <StepCard panel={panel}>
          <AnimatePresence mode="wait" initial={false} custom={direction}>
            <motion.div
              key={current.slug}
              custom={direction}
              initial={{ opacity: 0, x: direction * offset }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -direction * offset }}
              transition={{ duration: reduced ? 0.15 : 0.36, ease: EASE_ARRAY }}
              className="flex min-h-0 flex-1 flex-col"
              data-testid="step-content"
              data-step={current.slug}
            >
              {body()}
            </motion.div>
          </AnimatePresence>
        </StepCard>
      )}
    </PageFrame>
  );
}

export { SubmitLabel };
